import * as React from "react";

import { Input } from "@/components/ui/input";

/** A non-negative amount field (money, hour thresholds - nothing on this
 *  page is ever negative). Fixes two problems a plain
 *  `<Input type="number" value={n} onChange={...Number(e.target.value)}>`
 *  has:
 *   1. `type="number"`'s spinner/min attribute doesn't stop someone typing
 *      "-5" directly - this clamps on every change instead.
 *   2. Deriving the input's value from a formatted number every keystroke
 *      means a leading zero never goes away (typing "1" after a 0 value
 *      renders "01" until blur) - this keeps the raw string as local state
 *      while typing, only syncing back to the numeric value on blur/change.
 */
export function AmountInput({
  id,
  value,
  onChange,
  className,
  min = 0,
  max = Infinity
}: {
  id?: string;
  value: number;
  onChange: (value: number) => void;
  className?: string;
  /** Floor an empty/cleared/sub-floor value falls back to on blur (and is
   *  clamped to while typing). Defaults to 0 - money amounts and limits are
   *  fine at 0 - but a caller like the full-day hour threshold, where 0
   *  isn't a meaningful value, can pass 1 instead. */
  min?: number;
  /** Ceiling an over-max value is clamped to, both while typing and on
   *  blur. Defaults to no ceiling — money amounts and limits have none —
   *  but a caller like the full-day hour threshold, where more than 24
   *  hours in a single day is nonsensical, can pass 24. */
  max?: number;
}) {
  const [text, setText] = React.useState(String(value));

  // Keep in sync when the value changes from outside (e.g. loading a saved
  // policy) — but not on every render, or it would fight the user's typing.
  React.useEffect(() => {
    setText(String(value));
  }, [value]);

  const clamp = (n: number) => Math.min(max, Math.max(min, n));

  const commit = (raw: string) => {
    const parsed = Number(raw);
    const clamped = Number.isFinite(parsed) ? clamp(parsed) : min;
    onChange(clamped);
    setText(String(clamped));
  };

  return (
    <Input
      id={id}
      type="number"
      min={min}
      max={Number.isFinite(max) ? max : undefined}
      className={className}
      value={text}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        // Only commit numeric-parseable input as it's typed (so an amount
        // updates live for save-on-blur callers); an empty/partial string
        // (e.g. mid-edit "-") is left alone until commit() on blur.
        if (raw !== "" && raw !== "-") {
          const parsed = Number(raw);
          if (Number.isFinite(parsed)) onChange(clamp(parsed));
        }
      }}
      onBlur={(e) => commit(e.target.value)}
      // type="number" hijacks the mouse wheel to bump the value when this
      // field happens to be focused - surprising while scrolling a page
      // that just happens to have this input under the cursor. Blurring on
      // wheel hands scrolling back to the page without changing the value.
      onWheel={(e) => e.currentTarget.blur()}
    />
  );
}
